import { useEffect, useRef, useState } from 'preact/hooks';

export interface ComposerProps {
  disabled: boolean;
  isLoading: boolean;
  placeholder: string;
  /** Resolves false when the send failed, which keeps the text in the box. */
  onSend: (question: string) => Promise<boolean>;
  /** Epoch ms until which sending is rate-limited, or null. */
  cooldownUntil?: number | null;
}

/** Whole seconds left until `until`, or 0 once it has passed. */
function secondsLeft(until: number | null | undefined): number {
  if (!until) return 0;
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

/** Grow the textarea to fit its content (wrapping to new lines), capped by its CSS max-height. */
function autoGrow(el: HTMLTextAreaElement | null): void {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
  // Only show the scrollbar once content exceeds the CSS max-height, so the
  // empty/short box doesn't display a scrollbar and its arrows.
  el.style.overflowY = el.scrollHeight > el.clientHeight ? 'auto' : 'hidden';
}

export function Composer({
  disabled,
  isLoading,
  placeholder,
  onSend,
  cooldownUntil,
}: ComposerProps) {
  const [value, setValue] = useState('');
  const [remaining, setRemaining] = useState(() => secondsLeft(cooldownUntil));
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus once the composer becomes enabled — i.e. right after the session is
  // created, mirroring the drawer's focusInput() calls after each async step.
  useEffect(() => {
    if (!disabled) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [disabled]);

  // Tick the rate-limit countdown down to zero, then stop. Cleared on unmount
  // and whenever a new cooldown replaces the old one.
  useEffect(() => {
    setRemaining(secondsLeft(cooldownUntil));
    if (!cooldownUntil) return;
    const id = setInterval(() => {
      const left = secondsLeft(cooldownUntil);
      setRemaining(left);
      if (left <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const cooling = remaining > 0;

  const send = async () => {
    const question = value.trim();
    if (!question || disabled || isLoading || cooling) return;
    // Clear immediately so sending feels instant — the question is already
    // echoed as a chat bubble, so leaving it in the box reads as "not sent yet".
    setValue('');
    // Collapse the textarea back to a single row now that it's been cleared.
    setTimeout(() => autoGrow(inputRef.current));

    const sent = await onSend(question);
    if (!sent) {
      // Put it back on failure (rate limit, dropped connection). useChat also
      // removes the pending bubble, so the question ends up in exactly one
      // place — the box — ready to resend rather than retyped from memory.
      setValue(question);
      setTimeout(() => autoGrow(inputRef.current));
    }
  };

  return (
    <div class="aj-drawer__input">
      <textarea
        ref={inputRef}
        rows={1}
        class="aj-drawer__field"
        value={value}
        onInput={e => {
          setValue((e.target as HTMLTextAreaElement).value);
          autoGrow(e.target as HTMLTextAreaElement);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
      />
      <button
        type="button"
        class="aj-drawer__send"
        onClick={send}
        disabled={!value.trim() || isLoading || disabled || cooling}
      >
        {cooling ? `Wait ${remaining}s` : 'Send'}
      </button>
    </div>
  );
}
